import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, object, string, number, mixed } from 'yup';
import * as yup from 'yup';

export type CreateProviderConfigFormState = {
  maxWin: number;
  defaultBet: number;
  minBet: number;
  maxBet: number;
  betSuggestions: any;
  minAutoCashout: number;
  maxAutoCashout: number;
  defaultAutoCashout: number;
  isRoundable: string | boolean;
  currency: string;
};

export const createProviderConfigFormStateDefault: CreateProviderConfigFormState = {
  maxWin: 0,
  defaultBet: 0,
  minBet: 0,
  maxBet: 0,
  betSuggestions: [],
  minAutoCashout: 0,
  maxAutoCashout: 0,
  defaultAutoCashout: 0,
  isRoundable: '',
  currency: '',
};

export const createProviderConfigSchemaDefault: ObjectSchema<
  Partial<CreateProviderConfigFormState>
> = object<Partial<CreateProviderConfigFormState>>().shape({
  maxWin: number().required('Max Win is required').positive('Max Win must be a positive number'),
  defaultBet: number()
    .required('Default Bet is required')
    .positive('Default Bet must be a positive number'),
  minBet: number()
    .required('Min Bet is required')
    .positive('Min Bet must be a positive number')
    .test('min-less-than-max', 'Min Bet must be less than Max Bet', function (value) {
      const { maxBet } = this.parent;
      // Skip comparison if maxBet is not set or is 0 (form not filled yet or reset state)
      if (!maxBet || maxBet === 0 || typeof maxBet !== 'number') {
        return true;
      }
      return value < maxBet;
    })
    .test(
      'min-bet-roundable',
      'Min Bet must be at least 1 when roundable is enabled',
      function (value) {
        const { isRoundable } = this.parent;
        if (isRoundable === true || isRoundable === 'true') {
          return value !== undefined && value >= 1;
        }
        return true;
      }
    ),
  maxBet: number()
    .required('Max Bet is required')
    .positive('Max Bet must be a positive number')
    .test('max-greater-than-min', 'Max Bet must be greater than Min Bet', function (value) {
      const { minBet } = this.parent;
      if (!minBet || minBet === 0 || typeof minBet !== 'number') {
        return true;
      }
      return value > minBet;
    }),
  betSuggestions: mixed().test(
    'is-valid',
    'Bet Suggestions must be a string, number, or an array of strings/numbers',
    (value) =>
      Array.isArray(value)
        ? value.every((v) => typeof v === 'string' || typeof v === 'number')
        : typeof value === 'string' || typeof value === 'number'
  ),
  minAutoCashout: number()
    .required('Min Auto Cashout is required')
    .positive('Min Auto Cashout must be a positive number')
    .test(
      'min-cashout-less-than-max',
      'Min Auto Cashout must be less than Max Auto Cashout',
      function (value) {
        const { maxAutoCashout } = this.parent;
        if (!maxAutoCashout || maxAutoCashout === 0 || typeof maxAutoCashout !== 'number') {
          return true;
        }
        return value < maxAutoCashout;
      }
    ),
  maxAutoCashout: number()
    .required('Max Auto Cashout is required')
    .positive('Max Auto Cashout must be a positive number')
    .test(
      'max-cashout-greater-than-min',
      'Max Auto Cashout must be greater than Min Auto Cashout',
      function (value) {
        const { minAutoCashout } = this.parent;
        if (!minAutoCashout || minAutoCashout === 0 || typeof minAutoCashout !== 'number') {
          return true;
        }
        return value > minAutoCashout;
      }
    ),
  defaultAutoCashout: number()
    .required('Default Auto Cashout is required')
    .positive('Default Auto Cashout must be a positive number')
    .test(
      'default-cashout-greater-than-min',
      'Default Auto Cashout must be greater than or equal to Min Auto Cashout',
      function (value) {
        const { minAutoCashout } = this.parent;
        if (!minAutoCashout || minAutoCashout === 0 || typeof minAutoCashout !== 'number') {
          return true;
        }
        return value >= minAutoCashout;
      }
    )
    .test(
      'default-cashout-less-than-max',
      'Default Auto Cashout must be less than or equal to Max Auto Cashout',
      function (value) {
        const { maxAutoCashout } = this.parent;
        if (!maxAutoCashout || maxAutoCashout === 0 || typeof maxAutoCashout !== 'number') {
          return true;
        }
        return value <= maxAutoCashout;
      }
    ),
  isRoundable: string().required('Boolean is required'),
  currency: string()
    .required('Currency is required')
    .min(3, 'Currency must be at least 3 characters long'),
});

type CreateProviderConfigFormContextType = {
  schema: ObjectSchema<Partial<CreateProviderConfigFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<CreateProviderConfigFormState>>>;
  methods?: UseFormReturn<CreateProviderConfigFormState>;
};

const createProviderConfigFormContextDefault = {
  schema: createProviderConfigSchemaDefault,
  setSchema: noop,
} satisfies CreateProviderConfigFormContextType;

export const CreateProviderConfigFormContext = createContext<CreateProviderConfigFormContextType>(
  createProviderConfigFormContextDefault
);

export const useCreateProviderConfigForm = () => {
  const { methods } = useContext(CreateProviderConfigFormContext);
  if (!methods) {
    throw new Error('CreateProviderConfigFormContext is empty');
  }

  return methods;
};

export const useManageCreateProviderConfigSchema = () => {
  const { setSchema } = useContext(CreateProviderConfigFormContext);

  return { setSchema };
};
